import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueries, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { format, startOfWeek } from "date-fns";
import { getBusinessToday } from "@/utils/businessTime";
import { fr } from "date-fns/locale";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  Add,
  Search,
  Visibility,
  Person,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  StatusBadge,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
  DateRangeFilter,
  type OrderStatus as DesignOrderStatus,
} from "@/components/ui";
import { orderService, Order, OrderStatus, type ProductTypeFilter, type ProductInPeriod } from "@/services/order.service";
import { weeklyMenuService } from "@/services/weeklyMenu.service";
import { useToastStore } from "@/store/toastStore";
import { usePersistedState } from "@/hooks/usePersistedState";
import { formatCustomerName } from "@/utils/formatCustomerName";

/**
 * Page de gestion des commandes
 * Liste avec recherche, filtres et actions
 * Basée sur la maquette success_notification_feedback et défi_gourmand_admin_dashboard_1
 */
interface OrdersLocationState {
  customerId?: string;
  customerName?: string;
  /** Date de livraison pour filtrer la liste après création de commande */
  deliveryDate?: string;
  /** Filtre de statut (ex. depuis l’alerte « Commandes en attente » du dashboard) */
  statusFilter?: OrderStatus | "all";
}

function parseLocalDateOnly(dateStr: string): Date {
  const s = dateStr.slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return new Date(NaN);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function toSlotDateKey(date: string | Date): string {
  if (typeof date === "string") return date.slice(0, 10);
  return new Date(date).toISOString().slice(0, 10);
}

const PRODUCT_TYPE_FILTER_OPTIONS: { value: ProductTypeFilter | ""; label: string }[] = [
  { value: "", label: "Toutes catégories" },
  { value: "STARTER", label: "Entrées" },
  { value: "MAIN_COURSE", label: "Plats" },
  { value: "DESSERT", label: "Desserts" },
  { value: "DRINK", label: "Boissons" },
  { value: "SIDE", label: "Accompagnements" },
];

export function OrdersPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { error: showError, success } = useToastStore();

  const todayStr = getBusinessToday();
  const [searchQuery, setSearchQuery] = usePersistedState("orders.searchQuery", "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState<OrderStatus | "all">("orders.statusFilter", "all");
  const [dateFrom, setDateFrom] = usePersistedState("orders.dateFrom", todayStr);
  const [dateTo, setDateTo] = usePersistedState("orders.dateTo", todayStr);
  const [customerIdFilter, setCustomerIdFilter] = usePersistedState<string | null>("orders.customerIdFilter", null);
  const [customerNameFilter, setCustomerNameFilter] = usePersistedState<string | null>("orders.customerNameFilter", null);
  const [productTypeFilter, setProductTypeFilter] = usePersistedState<ProductTypeFilter | "">("orders.productTypeFilter", "");
  const [productIdFilter, setProductIdFilter] = usePersistedState<string | null>("orders.productIdFilter", null);
  const [, setProductFilterLabel] = useState<string | null>(null);
  const [page, setPage] = usePersistedState("orders.page", 1);
  const [rowsPerPage, setRowsPerPage] = usePersistedState("orders.rowsPerPage", 10);
  const [cancelConfirm, setCancelConfirm] = useState<{
    orderId: string;
    orderNumber: string;
  } | null>(null);

  // Appliquer le filtre client quand on arrive depuis la fiche client ("Voir tout")
  useEffect(() => {
    const state = location.state as OrdersLocationState | undefined;
    if (state?.customerId) {
      setCustomerIdFilter(state.customerId);
      setCustomerNameFilter(state.customerName ?? null);
      setDateFrom("");
      setDateTo("");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.customerId, location.pathname, navigate]);

  // Appliquer la date de livraison quand on arrive après création de commande
  useEffect(() => {
    const state = location.state as OrdersLocationState | undefined;
    if (state?.deliveryDate) {
      setDateFrom(state.deliveryDate);
      setDateTo(state.deliveryDate);
      setPage(1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.deliveryDate, location.pathname, navigate]);

  // Appliquer le filtre statut quand on arrive depuis le dashboard (alerte « Commandes en attente »)
  useEffect(() => {
    const state = location.state as OrdersLocationState | undefined;
    if (state?.statusFilter !== undefined) {
      setStatusFilter(state.statusFilter);
      setPage(1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.statusFilter, location.pathname, navigate, setStatusFilter]);

  // Debounce recherche (400 ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Remettre en page 1 quand filtres ou recherche changent
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch, dateFrom, dateTo, customerIdFilter, productTypeFilter, productIdFilter, rowsPerPage]);

  // Réinitialiser le produit uniquement quand l'utilisateur change la catégorie (pas au montage, pour garder la persistance)
  const prevProductTypeFilter = useRef(productTypeFilter);
  useEffect(() => {
    if (prevProductTypeFilter.current !== productTypeFilter) {
      prevProductTypeFilter.current = productTypeFilter;
      setProductIdFilter(null);
      setProductFilterLabel(null);
    }
  }, [productTypeFilter]);

  // Réinitialiser le produit uniquement quand l'utilisateur change la période (pas au montage)
  const prevDateRange = useRef({ dateFrom, dateTo });
  useEffect(() => {
    if (prevDateRange.current.dateFrom !== dateFrom || prevDateRange.current.dateTo !== dateTo) {
      prevDateRange.current = { dateFrom, dateTo };
      setProductIdFilter(null);
      setProductFilterLabel(null);
    }
  }, [dateFrom, dateTo]);

  // Produits proposés sur la période (présents dans au moins une commande sur la période)
  const { data: productsForFilter = [] } = useQuery({
    queryKey: ["orders", "products-in-period", dateFrom, dateTo, productTypeFilter],
    queryFn: () =>
      orderService.getProductsInPeriod(
        dateFrom,
        dateTo,
        productTypeFilter || undefined
      ),
    enabled: !!dateFrom && !!dateTo,
    placeholderData: keepPreviousData,
  });

  // Récupérer les commandes (pagination côté serveur)
  const {
    data: paginated,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["orders", statusFilter, debouncedSearch, dateFrom, dateTo, customerIdFilter, productTypeFilter, productIdFilter, page, rowsPerPage],
    queryFn: () =>
      orderService.getAll({
        status: statusFilter === "all" ? undefined : statusFilter,
        search: debouncedSearch || undefined,
        customerId: customerIdFilter || undefined,
        deliveryDateFrom: dateFrom || undefined,
        deliveryDateTo: dateTo || undefined,
        productType: productTypeFilter || undefined,
        productId: productIdFilter || undefined,
        page,
        limit: rowsPerPage,
      }),
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const orders = useMemo(() => {
    if (!paginated) return [];
    return Array.isArray(paginated) ? paginated : paginated.orders;
  }, [paginated]);
  const total = useMemo(() => {
    if (!paginated || Array.isArray(paginated)) return orders.length;
    return paginated.total;
  }, [paginated, orders.length]);

  /** Lundis des semaines couvertes par les commandes affichées (pour charger les menus) */
  const weekStartsFromOrders = useMemo(() => {
    const set = new Set<string>();
    for (const o of orders) {
      const d = o.deliveryDate?.slice(0, 10);
      if (!d) continue;
      set.add(format(startOfWeek(parseLocalDateOnly(d), { weekStartsOn: 1 }), "yyyy-MM-dd"));
    }
    return [...set];
  }, [orders]);

  const menuQueries = useQueries({
    queries: weekStartsFromOrders.map((weekStart) => ({
      queryKey: ["weekly-menu", "orders-list", weekStart],
      queryFn: () => weeklyMenuService.getByWeekIfExists(weekStart),
      staleTime: 60_000,
      enabled: weekStartsFromOrders.length > 0,
    })),
  });

  const menuByWeekStart = useMemo(() => {
    const m = new Map<string, Awaited<ReturnType<typeof weeklyMenuService.getByWeek>>>();
    weekStartsFromOrders.forEach((ws, i) => {
      const d = menuQueries[i]?.data;
      if (d) m.set(ws, d);
    });
    return m;
  }, [menuQueries, weekStartsFromOrders]);

  /** Clés "YYYY-MM-DD|productId" où le menu du jour a l’option Switch cochée sur le créneau PLATS */
  const switchFromMenuKeys = useMemo(() => {
    const set = new Set<string>();
    for (const o of orders) {
      const ds = o.deliveryDate?.slice(0, 10);
      if (!ds) continue;
      const monday = format(startOfWeek(parseLocalDateOnly(ds), { weekStartsOn: 1 }), "yyyy-MM-dd");
      const menu = menuByWeekStart.get(monday);
      if (!menu?.slots?.length) continue;
      for (const s of menu.slots) {
        if (s.slotType !== "PLATS" || !s.isSwitch || !s.productId) continue;
        if (toSlotDateKey(s.date as string | Date) !== ds) continue;
        set.add(`${ds}|${s.productId}`);
      }
    }
    return set;
  }, [orders, menuByWeekStart]);

  // Gérer les erreurs avec useEffect
  useEffect(() => {
    if (queryError) {
      const err = queryError as unknown as { response?: { status?: number }; message?: string };
      if (err?.response?.status !== 404) {
        showError(
          (err?.response as { data?: { message?: string } })?.data?.message ||
            "Erreur lors du chargement des commandes",
        );
      }
    }
  }, [queryError, showError]);

  const handleViewOrder = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const cancelMutation = useMutation({
    mutationFn: (orderId: string) => orderService.cancel(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      success("Commande annulée avec succès");
      setCancelConfirm(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } };
      showError(
        e?.response?.data?.error?.message ??
          e?.response?.data?.message ??
          "Erreur lors de l'annulation de la commande",
      );
    },
  });

  const handleCancelOrder = (order: Order) => {
    setCancelConfirm({ orderId: order.id, orderNumber: order.orderNumber });
  };

  const handleCancelConfirm = () => {
    if (cancelConfirm) cancelMutation.mutate(cancelConfirm.orderId);
  };

  const handleCancelDialogClose = () => setCancelConfirm(null);

  const apiStatusToDesignStatus = (status: OrderStatus): DesignOrderStatus => {
    const map: Record<OrderStatus, DesignOrderStatus> = {
      PENDING: "pending",
      CONFIRMED: "confirmed",
      IN_PREPARATION: "inProgress",
      READY: "ready",
      IN_DELIVERY: "inDelivery",
      DELIVERED: "delivered",
      CANCELLED: "cancelled",
    };
    return map[status] ?? "pending";
  };

  // Liste des statuts pour les filtres
  const statusChips: Array<{ value: OrderStatus | "all"; label: string }> = [
    { value: "all", label: "Toutes" },
    { value: "PENDING", label: "En Attente" },
    { value: "IN_PREPARATION", label: "En Préparation" },
    { value: "DELIVERED", label: "Livrées" },
    { value: "CANCELLED", label: "Annulées" },
  ];

  if (isLoading) {
    return <LoadingSpinner message="Chargement des commandes..." />;
  }

  // Si erreur 404, on affiche un état vide avec message informatif
  if (queryError && (queryError as any)?.response?.status === 404) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader
          title="Commandes"
          subtitle="Gérez le traitement et la logistique des commandes traiteur"
          action={
            <Button
              variant="primary"
              startIcon={<Add />}
              onClick={() =>
                navigate("/orders/new", {
                  state: { deliveryDate: dateFrom || undefined },
                })
              }
            >
              Nouvelle Commande
            </Button>
          }
        />
        <EmptyState
          title="Backend non disponible"
          description="Les endpoints pour les commandes ne sont pas encore implémentés dans le backend. Veuillez créer les routes API avant d'utiliser cette page."
        />
      </Box>
    );
  }

  if (queryError && (queryError as any)?.response?.status !== 404) {
    return <ErrorMessage message="Erreur lors du chargement des commandes." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Commandes"
        subtitle="Gérez le traitement et la logistique des commandes traiteur"
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() =>
              navigate("/orders/new", {
                state: { deliveryDate: dateFrom || undefined },
              })
            }
          >
            Nouvelle Commande
          </Button>
        }
      />

      <FiltersCard>
        <InputField
            placeholder="Rechercher une commande, un client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            icon={<Search />}
            iconPosition="start"
            sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
          />

          {/* Filtres par statut (chips) */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {statusChips.map((chip) => (
              <Button
                key={chip.value}
                variant={statusFilter === chip.value ? "primary" : "outline"}
                size="small"
                onClick={() => setStatusFilter(chip.value)}
                sx={{
                  borderRadius: "9999px",
                  minWidth: "auto",
                  px: 2,
                }}
              >
                {chip.label}
              </Button>
            ))}
          </Box>

          {/* Filtre par date de livraison */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChangeFrom={setDateFrom}
              onChangeTo={setDateTo}
              showNavArrows
              showTodayButton
              labelFrom="Du"
              labelTo="Au"
              size="small"
            />
            {/* Filtre par catégorie de produit */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="orders-filter-product-type">Catégorie</InputLabel>
              <Select
                labelId="orders-filter-product-type"
                label="Catégorie"
                value={productTypeFilter}
                onChange={(e) =>
                  setProductTypeFilter((e.target.value || "") as ProductTypeFilter | "")
                }
              >
                {PRODUCT_TYPE_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Filtre par produit (uniquement les produits de la période) */}
            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={productsForFilter}
              getOptionLabel={(p: ProductInPeriod) => p.name}
              value={productsForFilter.find((p) => p.id === productIdFilter) ?? null}
              onChange={(_, value) => {
                setProductIdFilter(value?.id ?? null);
                setProductFilterLabel(value?.name ?? null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Produit"
                  placeholder={dateFrom && dateTo ? "Produits de la période" : "Choisir une période"}
                />
              )}
              disabled={!dateFrom || !dateTo}
            />
            {(dateFrom || dateTo || statusFilter !== "all" || customerIdFilter || productTypeFilter || productIdFilter || searchQuery.trim()) && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  const today = getBusinessToday();
                  setDateFrom(today);
                  setDateTo(today);
                  setStatusFilter("all");
                  setCustomerIdFilter(null);
                  setCustomerNameFilter(null);
                  setProductTypeFilter("");
                  setProductIdFilter(null);
                  setProductFilterLabel(null);
                  setSearchQuery("");
                  setPage(1);
                }}
                sx={{ textTransform: "none" }}
              >
                Effacer les filtres
              </Button>
            )}
          </Box>
          {/* Filtre par client (depuis fiche client "Voir tout") */}
          {customerIdFilter && (
            <Chip
              icon={<Person />}
              label={customerNameFilter ? `Client : ${customerNameFilter}` : "Filtre client actif"}
              onDelete={() => {
                setCustomerIdFilter(null);
                setCustomerNameFilter(null);
              }}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
      </FiltersCard>

      {/* Tableau des commandes */}
      {orders.length === 0 && !isLoading ? (
        <EmptyState
          title="Aucune commande trouvée"
          description={
            debouncedSearch ||
            statusFilter !== "all" ||
            dateFrom ||
            dateTo ||
            customerIdFilter ||
            productTypeFilter ||
            productIdFilter
              ? "Aucune commande ne correspond aux critères de recherche."
              : "Il n'y a aucune commande pour le moment. Créez-en une nouvelle !"
          }
          action={
            !debouncedSearch &&
            statusFilter === "all" &&
            !dateFrom &&
            !dateTo &&
            !customerIdFilter &&
            !productTypeFilter &&
            !productIdFilter
              ? {
                  label: "Créer une commande",
                  onClick: () =>
                    navigate("/orders/new", {
                      state: { deliveryDate: dateFrom || undefined },
                    }),
                }
              : undefined
          }
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Référence
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Client
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Lieu de Livraison
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                      maxWidth: 280,
                    }}
                  >
                    Produits
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Qté
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Statut
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Montant
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontFamily: "monospace" }}
                          >
                            {order.orderNumber}
                          </Typography>
                          {order.status === "IN_PREPARATION" && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                animation: "pulse 2s infinite",
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCustomerName(order.customer ? {
                            firstName: order.customer.firstName,
                            lastName: order.customer.lastName,
                            type: order.customer.type as "INDIVIDUAL" | "INSTITUTION" | "COMPANY",
                          } : undefined) || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {order.customerAddress?.zone?.name ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {order.deliveryDate
                            ? format(
                                new Date(order.deliveryDate),
                                "dd MMM yyyy",
                                { locale: fr },
                              )
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, verticalAlign: "top" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, py: 0.5 }}>
                          {(order.items ?? []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            (order.items ?? []).map((item, idx) => {
                              const ds = order.deliveryDate?.slice(0, 10) ?? "";
                              const showSwitch =
                                !!ds &&
                                switchFromMenuKeys.has(`${ds}|${item.productId}`);
                              return (
                                <Box
                                  key={item.id ?? `${order.id}-${item.productId}-${idx}`}
                                  sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
                                >
                                  <Typography variant="caption" component="span" sx={{ lineHeight: 1.4 }}>
                                    {item.product?.name ?? "Produit"} ×{item.quantity}
                                  </Typography>
                                  {showSwitch && (
                                    <Chip
                                      label="Switch"
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        bgcolor: "#fef3c7",
                                        color: "#b45309",
                                        border: "1px solid #fde68a",
                                      }}
                                    />
                                  )}
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                          {order.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <StatusBadge
                          status={apiStatusToDesignStatus(order.status)}
                          showDot={order.status === "IN_PREPARATION"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontFamily: "monospace" }}
                        >
                          {order.totalAmount.toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          €
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrder(order.id)}
                          aria-label="Voir les détails"
                        >
                          <Visibility />
                        </IconButton>
                        {order.status !== "CANCELLED" && (
                          <IconButton
                            size="small"
                            onClick={() => handleCancelOrder(order)}
                            aria-label="Annuler la commande"
                          >
                            <CancelIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Lignes par page :"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!cancelConfirm}
        title="Annuler la commande"
        message={
          cancelConfirm
            ? `Êtes-vous sûr de vouloir annuler la commande ${cancelConfirm.orderNumber} ? Cette action est irréversible.`
            : ""
        }
        confirmText="Annuler la commande"
        cancelText="Retour"
        variant="danger"
        loading={cancelMutation.isPending}
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelDialogClose}
      />
    </Box>
  );
}
